import { logStore } from "./logStore";
import { SCHEMA_VERSION } from './types';
import type { LogCategory, LogContext, LogWriteOptions } from './types';
import { getCurrentRoute } from './currentRoute';
import { buildNumber, environment, offline } from './runtimeContext';
import { generateUUID } from "@/modules/uuid";

const LOG_SESSION_STORAGE_KEY = 'thinkmd_log_session_id';

interface PiniaLike {
    state?: { value?: { encounter?: { uuid?: string } } };
}

// read off the global: importing pinia here drags its ESM plugin into every suite
function getEncounterSessionId(): string | undefined {
    const globalApp = (
        globalThis as typeof globalThis & { App?: { $pinia?: PiniaLike } }
    ).App;
    const uuid = globalApp?.$pinia?.state?.value?.encounter?.uuid;
    return typeof uuid === 'string' && uuid ? uuid : undefined;
}

function getOrCreateLogSessionId(): string | undefined {
    try {
        const existing = globalThis.sessionStorage.getItem(LOG_SESSION_STORAGE_KEY);
        if (existing) {
            return existing;
        }

        const generated = generateUUID();
        globalThis.sessionStorage.setItem(LOG_SESSION_STORAGE_KEY, generated);
        return generated;
    } catch {
        return undefined;
    }
}

function withSessionIdentifiers(options?: LogWriteOptions): LogWriteOptions {
    const enriched = { ...options };

    if (!enriched.sessionId) {
        enriched.sessionId = getEncounterSessionId();
    }

    if (!enriched.logSessionId) {
        enriched.logSessionId = getOrCreateLogSessionId();
    }

    if (enriched.schemaVersion === undefined) {
        enriched.schemaVersion = SCHEMA_VERSION;
    }

    if (enriched.buildNumber === undefined) {
        enriched.buildNumber = buildNumber();
    }

    if (enriched.environment === undefined) {
        enriched.environment = environment();
    }

    if (enriched.offline === undefined) {
        enriched.offline = offline();
    }

    if (enriched.route === undefined) {
        enriched.route = getCurrentRoute();
    }

    return enriched;
}

export const logger = {
    error(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'ERROR',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    warn(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'WARN',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    info(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'INFO',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    debug(
        category: LogCategory,
        event: string,
        context?: LogContext,
        message?: string,
        options?: LogWriteOptions,
    ) {
        return logStore.write(
            'DEBUG',
            category,
            event,
            context,
            message,
            withSessionIdentifiers(options),
        );
    },

    /**
     * Returns whether diagnostic mode is active.
     * @returns {boolean}
     */
    get diagnosticMode() {
        return logStore.diagnosticMode;
    },

    /**
     * Enables or disables diagnostic logging.
     * @param {boolean} enabled
     */
    setDiagnosticMode(enabled: boolean) {
        logStore.setDiagnosticMode(enabled);
    }
}
